import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchApplications, fetchApplication, postApplication } from "../../../store/reducers/applicationSlice";
import { getAttachments } from "../../../store/reducers/fileSlice";
import { showSuccess, showError } from '../../../helpers/toast';
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import { Link } from "react-router-dom";
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import iconSearch from "../../../assets/media/icon-search.svg";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

const AppApprovedApp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;

  const applications = useSelector((state) => state.applications);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  // Fetch Applications Data
  useEffect(() => {
    let queryParams = {
      state: 'approved' // Add state filter to API call
    };

    // SECURITY: Add company_id filter at API level for company_admin
    if (user.user?.user_role === "company_admin" && user.user?.company_id) {
      queryParams.company_id = user.user.company_id;
    }
    // SECURITY: Add user_id filter at API level for employees
    else if (user.user?.user_role === "employee" && user.user?.id) {
      queryParams.user_id = user.user.id;
    }

    if (startDate && endDate) {
      queryParams.start_date = startDate.toISOString();
      queryParams.end_date = endDate.toISOString();
    }
    console.log('Fetching approved applications with params:', queryParams);
    dispatch(
      fetchApplications({
        accessToken: authToken,
        queryParams: queryParams,
        fetchAllPages: true // Fetch all pages to show all applications
      })
    );
  }, [authToken, dispatch, startDate, endDate, user.user?.user_role, user.user?.company_id, user.user?.id]);

  // PERFORMANCE: Optimized merge and filter using useMemo and Map for O(1) lookups
  const mergedAndFilteredData = useMemo(() => {
    if (!applications.data?.length || !getallUserdata?.length) {
      return [];
    }

    // PERFORMANCE: Create a Map for O(1) user lookup instead of O(n) find()
    const userMap = new Map(getallUserdata.map(user => [user.id, user]));
    const userRole = user.user?.user_role;
    const currentUserId = user.user?.id;
    const userCompanyId = user.user?.company_id;

    // PERFORMANCE: Filter and merge in one pass to avoid multiple iterations
    const filteredData = applications.data
      .filter((appItem) => {
        // Early exit if state doesn't match
        if (appItem.state !== "approved") return false;

        const itemCompanyId = appItem.company_id || appItem.companyId;
        const itemUserId = appItem.user_id || appItem.userId || appItem.user?.id;

        // SECURITY: If company_id is missing, don't show the application
        if (!itemCompanyId || !userCompanyId) return false;

        // Role-based filtering
        if (userRole === "company_admin") {
          return String(itemCompanyId) === String(userCompanyId);
        } else if (userRole === "employee") {
          const isOwnApplication = String(itemUserId) === String(currentUserId);
          const hasCorrectCompany = String(itemCompanyId) === String(userCompanyId);
          return isOwnApplication && hasCorrectCompany;
        } else {
          return String(itemCompanyId) === String(userCompanyId);
        }
      })
      .map((appItem) => {
        const userIdFromApp = appItem.user_id || appItem.userId || appItem.user?.id;
        const userItem = userMap.get(userIdFromApp); // O(1) lookup instead of O(n) find()
        const finalUserId = userItem?.id || userIdFromApp;
        const finalCompanyId = appItem.company_id || appItem.companyId || userItem?.company_id || user.user?.company_id;

        if (userItem) {
          return {
            ...appItem,
            applicationId: appItem.id,
            ...userItem,
            user_id: finalUserId,
            userId: userItem.id,
            company_id: finalCompanyId,
          };
        }
        return {
          ...appItem,
          applicationId: appItem.id,
          user_id: finalUserId,
          company_id: finalCompanyId,
        };
      });

    return filteredData;
  }, [applications.data, getallUserdata, user.user?.user_role, user.user?.company_id, user.user?.id]);

  // Apply search filter separately
  useEffect(() => {
    if (!searchQuery) {
      setFilteredApplication(mergedAndFilteredData);
      return;
    }

    const searchLower = searchQuery.toLowerCase();
    const searched = mergedAndFilteredData.filter((item) =>
      Object.values(item).some(
        (value) =>
          value &&
          value.toString().toLowerCase().includes(searchLower)
      )
    );
    setFilteredApplication(searched);
    setCurrentPage(1);
  }, [searchQuery, mergedAndFilteredData]);


  // Pagination data
  const paginatedData = useMemo(() => {
    return filteredApplication
      .sort((a, b) => b.applicationId - a.applicationId)
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredApplication, currentPage, itemsPerPage]);

  const getTotalPages = () => Math.ceil(filteredApplication.length / itemsPerPage);

  // Download
  const handleDownload = useCallback(async (item) => {
    const { applicationId, userId } = item;

    // Prevent multiple clicks
    if (downloadingIds.includes(applicationId)) {
      return;
    }

    setDownloadingIds((prev) => [...prev, applicationId]);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}file/${userId}/downloadapplicationfiles/${applicationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/zip",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `application_${applicationId}_files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccess('Files downloaded successfully!');
    } catch (error) {
      console.error("Download failed", error);
      showError(`Download failed: ${error.message}`);
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== applicationId));
    }
  }, [authToken, downloadingIds]);

  // Create new application from previous data
  const handleCreateNewApplication = useCallback(async (row) => {
    try {
      const applicationId = row.applicationId || row.id;
      const userId = row.userId || row.user_id || user.user?.id;

      showSuccess('Fetching previous application data...');

      // Fetch the previous application data
      const applicationResponse = await dispatch(fetchApplication({
        id: applicationId,
        accessToken: authToken
      }));

      if (!applicationResponse.payload) {
        showError('Failed to fetch previous application data');
        return;
      }

      const previousApplication = applicationResponse.payload.application ||
        applicationResponse.payload.applications?.[0] ||
        applicationResponse.payload;

      if (!previousApplication) {
        showError('Application data not found');
        return;
      }

      // Parse json_data if it's a string, handle both string and object cases
      let jsonData = previousApplication.json_data;
      if (typeof jsonData === 'string') {
        // Check if it's a valid JSON string (not "[object Object]")
        if (jsonData.trim().startsWith('{') || jsonData.trim().startsWith('[')) {
          try {
            jsonData = JSON.parse(jsonData);
          } catch (e) {
            console.error('Error parsing json_data:', e);
            jsonData = {};
          }
        } else {
          // Handle cases like "[object Object]" - use empty object
          console.warn('Invalid json_data format, using empty object');
          jsonData = {};
        }
      } else if (typeof jsonData === 'object' && jsonData !== null) {
        // Already an object, use as-is (no conversion needed)
      } else {
        // Fallback to empty object
        jsonData = {};
      }

      // Get attachment file names to pass to backend for copying
      let attachmentFiles = [];
      try {
        const attachments = await getAttachments({
          userId: userId,
          applicationId: applicationId,
          authToken: authToken
        });
        console.log('Fetched attachments from source application:', attachments);
        if (attachments && Array.isArray(attachments) && attachments.length > 0) {
          // Pass file names as objects - backend will copy them from source application
          attachmentFiles = attachments.map(filename => ({ name: filename }));
          console.log('Prepared attachment files for copying:', attachmentFiles);
        } else {
          console.log('No attachments found or empty array');
        }
      } catch (error) {
        console.error('Could not fetch attachments, continuing without them:', error);
      }

      // Create new application with previous data
      showSuccess('Creating new application...');

      console.log('Creating application with:', {
        sourceApplicationId: applicationId,
        sourceUserId: userId,
        attachmentFilesCount: attachmentFiles.length,
        attachmentFiles: attachmentFiles.map(f => f.name)
      });

      const newApplicationResponse = await dispatch(postApplication({
        form: previousApplication.name || previousApplication.form,
        json_data: jsonData,
        name: previousApplication.name,
        category: previousApplication.category || previousApplication.categoryName,
        user_id: user.user.id,
        token: authToken,
        files: attachmentFiles, // File names - backend will copy from sourceApplicationId
        sourceApplicationId: applicationId, // Backend will copy files from this application
        sourceUserId: userId, // Source user ID for file copying
        state: 'draft' // Create as draft so user can edit before submitting
      }));

      if (newApplicationResponse.payload && newApplicationResponse.payload.id) {
        const newApplicationId = newApplicationResponse.payload.id;
        showSuccess('New application created successfully!');

        // Navigate to the renewal page with the new application ID
        navigate(`/app-renewal/${newApplicationId}`);
      } else {
        showError('Failed to create new application');
      }
    } catch (error) {
      console.error('Error creating new application:', error);
      showError(`Error creating new application: ${error.message}`);
    }
  }, [dispatch, authToken, user, navigate]);

  // Table Columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("applicationId", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.name || row.applicationName || 'N/A'}</strong>
              <span>{row.categoryName || row.category || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedBy",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>{row.companyEmail || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.userName || row.user_name || row.name || 'N/A'}</strong>
              <span>{row.userEmail || row.user_email || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("updated_at", {
        header: "Submitted Date",
        cell: (info) => (
          <span className="dateText">{info.getValue()?.slice(0, 10)}</span>
        ),
      }),
      columnHelper.accessor("issue_date", {
        header: "Registered Date",
        cell: (info) => (
          <span className="dateText">{info.getValue()?.slice(0, 10)}</span>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    <Link
                      onClick={() => {
                        if (!downloadingIds.includes(row.applicationId)) {
                          handleDownload(row);
                        }
                      }}
                      className="dropdown-item"
                      style={{
                        opacity: downloadingIds.includes(row.applicationId) ? 0.6 : 1,
                        pointerEvents: downloadingIds.includes(row.applicationId) ? 'none' : 'auto',
                        cursor: downloadingIds.includes(row.applicationId) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {downloadingIds.includes(row.applicationId) ? 'Downloading...' : 'Download'}
                    </Link>
                  </li>
                  <li>
                    <Link
                      onClick={(e) => {
                        e.preventDefault();
                        handleCreateNewApplication(row);
                      }}
                      className="dropdown-item"
                    >
                      Create new appllication
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [downloadingIds, currentPage, itemsPerPage, handleDownload, getallUserdata, handleCreateNewApplication]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Add loading state
  if (applications.fetchLoading) {
    return (
      <div className="mainApp">
        <Navbar />
        <Header />
        <div className="wrapper">
          <div className="boxAppBoard boxWhite">
            <div className="text-center p-4">
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show message if no data is available
  if (!applications.data || applications.data.length === 0) {
    return (
      <div className="mainApp">
        <Navbar />
        <Header />
        <div className="wrapper">
          <div className="boxAppBoard boxWhite">
            <div className="text-center p-4">
              <p>No applications data available. Please check your connection or try again later.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader" style={{ flexWrap: "wrap", gap: "10px" }}>
            <div className="boardHeadLeft">
              <h2>Approved Applications</h2>
            </div>
            <div
              className="boardHeadRight"
              style={{
                gap: "10px",
                flexWrap: "wrap",
                justifyContent: "flex-start",
              }}
            >
              <form className="searchForm">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={(e) => setSearchQuery(e.target.value)}
                  value={searchQuery}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </form>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => setDateRange(update)}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="yyyy/MM/dd"
                />
              </div>
            </div>
          </div>

          {/* Table Head */}
          <div className="boardContent table-responsive">
            <table className="table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr className="" key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      <p>No approved applications found for your company</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>


          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
          />
        </div>
      </div>
    </div>
  );
};

export default AppApprovedApp;